import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  Shield, 
  Users, 
  CheckSquare, 
  FileText, 
  Calendar,
  Download,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Help() {
  const downloadGuide = async (guideType) => {
    try {
      toast.info(`Generating ${guideType === 'user' ? 'User' : 'Admin'} Guide PDF...`);
      const response = await fetch(`${BACKEND_URL}/api/reports/pdf/${guideType}-guide`);
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HLRA_${guideType === 'user' ? 'User' : 'Admin'}_Guide.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${guideType === 'user' ? 'User' : 'Admin'} Guide downloaded!`);
    } catch (error) {
      console.error('Failed to download guide:', error);
      toast.error('Failed to download guide');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="help-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Help & Documentation</h1>
        <p className="text-gray-600">Guides and resources for using the HLRA Action Portal</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User Guide */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              End User Guide
            </CardTitle>
            <CardDescription>
              Complete guide for all users covering daily operations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-green-500" />
                Creating and managing actions
              </p>
              <p className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-green-500" />
                Using reports and exports
              </p>
              <p className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-green-500" />
                Meeting management basics
              </p>
              <p className="flex items-center gap-2">
                <Users className="w-4 h-4 text-green-500" />
                Understanding roles and permissions
              </p>
            </div>
            <Button 
              onClick={() => downloadGuide('user')}
              className="w-full bg-blue-500 hover:bg-blue-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Download User Guide
            </Button>
          </CardContent>
        </Card>

        {/* Admin Guide */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-600" />
              Administrator Guide
            </CardTitle>
            <CardDescription>
              Comprehensive guide for system administrators
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <Users className="w-4 h-4 text-green-500" />
                User management and roles
              </p>
              <p className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                Database backup and maintenance
              </p>
              <p className="flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-green-500" />
                Action archiving and cleanup
              </p>
              <p className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-green-500" />
                Deployment checklist
              </p>
            </div>
            <Button 
              onClick={() => downloadGuide('admin')}
              className="w-full bg-purple-500 hover:bg-purple-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Admin Guide
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Action Statuses</h4>
              <ul className="text-sm space-y-1 text-gray-600">
                <li><span className="font-medium">Not Started</span> - Work hasn't begun</li>
                <li><span className="font-medium">In Progress</span> - Actively being worked on</li>
                <li><span className="font-medium">Completed</span> - Successfully finished</li>
                <li><span className="font-medium">Deferred</span> - Postponed to future</li>
                <li><span className="font-medium">Duplicate</span> - Duplicates another action</li>
                <li><span className="font-medium">Not Required</span> - No longer needed</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Priority Levels</h4>
              <ul className="text-sm space-y-1 text-gray-600">
                <li><span className="font-medium text-red-600">Urgent</span> - Immediate attention required</li>
                <li><span className="font-medium text-orange-600">High</span> - Important, address soon</li>
                <li><span className="font-medium text-yellow-600">Medium</span> - Standard priority</li>
                <li><span className="font-medium text-green-600">Low</span> - When time permits</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">User Roles</h4>
              <ul className="text-sm space-y-1 text-gray-600">
                <li><span className="font-medium">Super Admin</span> - Full system access</li>
                <li><span className="font-medium">Admin</span> - User & category management</li>
                <li><span className="font-medium">Committee</span> - Standard user access</li>
                <li><span className="font-medium">External Org</span> - Limited access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Need More Help?</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            If you have questions or encounter issues not covered in the documentation, 
            please contact your system administrator or the HLRA committee.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
